import { gql, useQuery } from "@apollo/client";
import classNames from "classnames";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMenuQuery } from "@repo/shopify/queries/others-gql";
import { menuQuery } from "@repo/shopify/queries/others-gql";

import Button from "@atoms/Button";
import Dim from "@atoms/Dim";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";

import HeaderAccount from "@organisms/Header/HeaderAccount";
import HeaderCart from "@organisms/Header/HeaderCart";
import HeaderMarket from "@organisms/Header/HeaderMarket";
import HeaderMenu from "@organisms/Header/HeaderMenu";
import HeaderNavigation from "@organisms/Header/HeaderNavigation";
import HeaderSearch from "@organisms/Header/HeaderSearch";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const activeURL = [URLS.SEARCH, URLS.PRODUCTS, URLS.TERMS_AND_CONDITIONS, URLS.PRIVACY_POLICY, URLS.ACCOUNT, URLS.NOT_FOUND, URLS.LEGAL];

export default function Header() {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const { isPC } = useCheckDevice();
  const { lang, market } = useLanguage();
  const {
    isCheckout,
    isVideoDetail,
    isDanielJohnston,
    isHome,
    isFeatures,
    isCollection,
    isSearch,
    isStore,
    isAccount,
    isOrderCompleted,
    isInformation,
    isRecruit,
  } = useCheckLocation();

  const {
    isCartOpen,
    openCart,
    openSearch,
    isSearchOpen,
    isAccountOpen,
    openAccount,
    isMarketOpen,
    openMarket,
    openNavigation,
    isNavigationOpen,
    openMenu,
    isMenuOpen,
    closeAll,
    openToast,
  } = useLayoutStore();

  const { token, caaToken } = useCustomerStore();
  const { cart } = useCartStore();

  const hasCustomerSession = lang === "jp" ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);

  const isMobileMenuOpen = isNavigationOpen || isSearchOpen || isAccountOpen || isCartOpen || isMarketOpen || isVideoDetail;

  // 모바일 메뉴는 헤더의 X 버튼으로 닫으므로, 이때만 헤더가 딤(z-50) 위로 올라가야 한다
  const isHeaderMenuOpen = !isPC && isMenuOpen && !isMobileMenuOpen;

  const { data: menuData } = useQuery<IMenuQuery>(gql(menuQuery.query), {
    variables: { handle: "navigation" },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  const cartTotalQuantity = useMemo(() => cart?.lines.edges.reduce((a, b) => a + b.node.quantity, 0) ?? 0, [cart]);

  const isActiveLink =
    activeURL.includes(pathname) ||
    isHome ||
    isOrderCompleted ||
    pathname.includes(URLS.ACCOUNT) ||
    pathname.includes(URLS.PRODUCTS) ||
    pathname.includes(URLS.RECRUIT);

  const isBackground =
    isMenuOpen ||
    isSearchOpen ||
    isAccountOpen ||
    isMarketOpen ||
    isFeatures ||
    isStore ||
    isCollection ||
    isSearch ||
    isAccount ||
    isInformation ||
    isRecruit;

  return (
    <>
      <header
        className={classNames(
          "tnt-gap-x pc:grid-cols-12 pc:px-5 pc:top-0 pc:h-12.5 pc:min-h-12.5 pc:items-end fixed top-0 mx-auto grid h-13.5 min-h-13.5 w-full grid-cols-8 items-center justify-between p-4 dark:text-white",
          isDanielJohnston
            ? ["pc:dark:bg-dj-black", (isCartOpen || isSearchOpen || isMarketOpen || isAccountOpen) && "dark:bg-black"]
            : "dark:bg-black",
          isHeaderMenuOpen ? "z-60" : "z-40",
          (isNavigationOpen || (!isPC && isBackground)) && ["bg-white", isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black"],
        )}
        id="header"
      >
        {isPC ? (
          <>
            <Link
              className={classNames("text-title font-features-normal col-span-2 flex w-fit leading-none", !isCheckout && "z-30")}
              href={URLS.HOME}
            >
              thisisneverthat®
            </Link>

            {!isCheckout && (
              <>
                {isVideoDetail ? (
                  <Link as="text" className="flex justify-end" href={URLS.VIDEO}>
                    Close
                  </Link>
                ) : (
                  <>
                    <div className="pc:col-span-7 col-span-5 flex gap-5 pb-0.5">
                      <div
                        className="relative flex h-full"
                        onMouseEnter={() => {
                          openNavigation(true);
                        }}
                        onMouseLeave={() => {
                          openNavigation(false);
                        }}
                      >
                        <Link
                          as="text"
                          href={URLS.COLLECTIONS_ALL}
                          isActive={pathname.includes(URLS.COLLECTIONS_DETAIL) || isNavigationOpen || isActiveLink}
                        >
                          Shop
                        </Link>
                        {isNavigationOpen ? <HeaderNavigation menu={menuData?.menu} /> : null}
                      </div>
                      <Link as="text" href={URLS.FEATURES} isActive={pathname.includes(URLS.FEATURES) || isActiveLink}>
                        Features
                      </Link>
                      <Link as="text" href={URLS.INFORMATION_CONTACT} isActive={pathname.includes(URLS.INFORMATION) || isActiveLink}>
                        Information
                      </Link>
                      <Link as="text" href={URLS.STORES} isActive={pathname.includes(URLS.STORES) || isActiveLink}>
                        Stores
                      </Link>
                    </div>

                    <div className="pc:z-20 col-span-3 flex justify-end gap-5 pb-0.5">
                      <Button
                        as="text"
                        onClick={() => {
                          openSearch(!isSearchOpen);
                          openNavigation(false);
                        }}
                      >
                        Search
                      </Button>
                      <Button
                        as="text"
                        onClick={() => {
                          if (hasCustomerSession) navigate(URLS.ORDER_HISTORY);
                          else openAccount(!isAccountOpen);
                          openNavigation(false);
                        }}
                      >
                        Account
                      </Button>
                      <Button
                        as="text"
                        onClick={() => {
                          openMarket(!isMarketOpen);
                        }}
                      >
                        {market}
                      </Button>
                      <Button
                        as="text"
                        className="flex flex-row gap-0.5"
                        onClick={() => {
                          openCart(!isCartOpen);
                          openNavigation(false);
                        }}
                      >
                        {cartTotalQuantity > 0 ? `Cart ${cartTotalQuantity}` : "Cart"}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          // Mobile
          <>
            {isCartOpen ? (
              <span className="text-subtitle col-span-5">
                {cart?.lines.edges.length > 0 ? `${cart?.lines.edges.length ?? 0} Items` : "Empty Cart"}
              </span>
            ) : (
              <>
                {isNavigationOpen ? (
                  <>
                    <Button
                      as="text"
                      className="col-span-1 flex"
                      onClick={() => {
                        openNavigation(false);
                        openMenu(true);
                      }}
                    >
                      <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
                    </Button>
                    <Button
                      as="text"
                      className="col-span-4 text-base!"
                      onClick={() => {
                        openNavigation(false);
                        openMenu(true);
                      }}
                    >
                      Back
                    </Button>
                  </>
                ) : (
                  <Link
                    className={classNames("text-title font-features-normal leading-none", isMenuOpen ? "col-span-5" : "col-span-5")}
                    href={URLS.HOME}
                  >
                    thisisneverthat®
                  </Link>
                )}
              </>
            )}

            {isVideoDetail ? (
              <Link as="text" className="z-70 col-span-3 flex! w-full justify-end!" href={URLS.VIDEO}>
                Close
              </Link>
            ) : null}

            {/* Mobile Right */}
            {!isCheckout && (
              <div className="col-span-3 flex items-center justify-end gap-6">
                {!isCartOpen && !isMobileMenuOpen ? (
                  <Button
                    as="text"
                    className="justify-end text-base!"
                    onClick={() => {
                      openCart(!isCartOpen);
                    }}
                  >
                    {cartTotalQuantity > 0 ? `Cart ${cartTotalQuantity}` : "Cart"}
                  </Button>
                ) : null}
                <Button
                  as="text"
                  className="size-fit justify-end font-bold!"
                  onClick={() => {
                    if (isMenuOpen) closeAll();
                    else openMenu(!isMenuOpen);
                  }}
                >
                  <Icon className="size-4.5" name={isMenuOpen ? "Close" : "Menu"} />
                </Button>
              </div>
            )}
          </>
        )}
      </header>

      {/* fixed 헤더가 비운 흐름 공간. 이게 없으면 예전처럼 main 이 헤더 높이만큼 위로 튄다 */}
      <div aria-hidden className="pc:h-12.5 h-13.5 w-full shrink-0" />

      {/* Mobile */}
      {isMenuOpen && !isMobileMenuOpen ? <HeaderMenu /> : null}
      {!isPC && isNavigationOpen ? <HeaderNavigation menu={menuData?.menu} /> : null}

      {isCartOpen ? <HeaderCart /> : null}
      {isSearchOpen ? <HeaderSearch /> : null}
      {isAccountOpen ? <HeaderAccount /> : null}
      {isMarketOpen ? <HeaderMarket /> : null}
      {isCartOpen || isSearchOpen || isAccountOpen || isMarketOpen ? <Dim /> : null}
    </>
  );
}
